import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight, CheckCircle, ArrowRight, ThumbsUp } from 'lucide-react';
import { api } from '../../config/api';
import { Link } from 'react-router-dom';

const fallbackReviews = [
  { display_name: 'Sarah M.', comment: 'The best dining experience in Seguku! The grilled chicken is absolutely divine. The ambiance makes you feel like you are in a five-star restaurant.', rating: 5, created_at: null },
  { display_name: 'James K.', comment: 'We celebrated our anniversary here and it was magical. The staff was attentive, the food was exquisite, and the atmosphere was perfect.', rating: 5, created_at: null },
  { display_name: 'Grace N.', comment: "From the moment you walk in, you feel the warmth. The kebabs are the best I've ever had. This is our family's go-to restaurant.", rating: 5, created_at: null },
  { display_name: 'David O.', comment: 'Exceptional service and even better food. The ribs were fall-off-the-bone tender. Will definitely be back with the whole family!', rating: 5, created_at: null },
  { display_name: 'Amara N.', comment: 'A hidden gem in Seguku! The portions are generous and the flavors are incredible. The grilled tilapia is a must-try.', rating: 5, created_at: null },
];

const avatarColors = ['#D4AF37', '#4CAF50', '#E74C3C', '#3498DB', '#9B59B6', '#1ABC9C', '#E67E22'];

const CARDS_BY_BREAKPOINT = { desktop: 3, tablet: 2, mobile: 1 };

const getVisibleCards = () => {
  if (window.innerWidth >= 992) return CARDS_BY_BREAKPOINT.desktop;
  if (window.innerWidth >= 576) return CARDS_BY_BREAKPOINT.tablet;
  return CARDS_BY_BREAKPOINT.mobile;
};

const Testimonials = () => {
  const [reviews, setReviews] = useState(fallbackReviews);
  const [stats, setStats] = useState({ total: 420, avg: 4.9, recommend: 98 });
  const [current, setCurrent] = useState(0);
  const [visibleCards, setVisibleCards] = useState(getVisibleCards());
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await api.get('/reviews.php?limit=20');
        if (res.data.success && res.data.data.length >= 2) {
          const data = res.data.data;
          setReviews(data);
          const avg = data.reduce((sum, r) => sum + Number(r.rating), 0) / data.length;
          const fiveStarCount = data.filter(r => Number(r.rating) >= 4).length;
          setStats({
            total: data.length >= 10 ? data.length : 420,
            avg: data.length >= 5 ? avg.toFixed(1) : 4.9,
            recommend: Math.round((fiveStarCount / data.length) * 100),
          });
        }
      } catch { /* use fallback */ }
    };
    fetchReviews();
  }, []);

  useEffect(() => {
    const handleResize = () => setVisibleCards(getVisibleCards());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, reviews.length - visibleCards);

  const goTo = useCallback((dir) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrent(prev => {
      if (dir === 'next') return prev >= maxIndex ? 0 : prev + 1;
      return prev <= 0 ? maxIndex : prev - 1;
    });
    setTimeout(() => setIsAnimating(false), 400);
  }, [isAnimating, maxIndex]);

  // Auto-slide
  useEffect(() => {
    if (reviews.length <= visibleCards || isPaused) return;
    intervalRef.current = setInterval(() => goTo('next'), 5000);
    return () => clearInterval(intervalRef.current);
  }, [reviews.length, visibleCards, isPaused, goTo]);

  const cardWidth = 100 / visibleCards;

  return (
    <section className="section-padding" style={{ backgroundColor: '#fff', overflow: 'hidden' }}>
      <div className="container">

        {/* Header */}
        <div className="text-center mb-5 reveal">
          <div className="d-flex align-items-center justify-content-center gap-1 mb-3">
            {[...Array(5)].map((_, i) => <Star key={i} size={20} fill="#D4AF37" color="#D4AF37" />)}
          </div>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 700, color: '#1A1A1A', marginBottom: '8px' }}>
            {stats.avg} — Based on {Number(stats.total) >= 100 ? `${stats.total}+` : `${stats.total}`} Reviews
          </h2>
          <div className="gold-divider-center" />
          <p style={{ fontSize: '1rem', color: '#777', marginTop: '8px' }}>What our guests are saying</p>
        </div>

        {/* Stats Bar */}
        <div style={{
          display: 'flex', justifyContent: 'center', gap: 'clamp(20px, 5vw, 64px)',
          flexWrap: 'wrap', marginBottom: '48px',
          backgroundColor: '#faf8f5', borderRadius: '16px', padding: '28px 32px',
          border: '1px solid rgba(212,175,55,0.15)'
        }}>
          {[
            { icon: <ThumbsUp size={22} color="#D4AF37" />, value: `${stats.recommend}%`, label: 'Recommend Us' },
            { icon: <Star size={22} fill="#D4AF37" color="#D4AF37" />, value: stats.avg, label: 'Average Rating' },
            { icon: <CheckCircle size={22} color="#4CAF50" />, value: `${Number(stats.total) >= 100 ? stats.total + '+' : stats.total}`, label: 'Verified Reviews' },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: 'center', minWidth: '100px' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '6px' }}>{s.icon}</div>
              <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.8rem', fontWeight: 700, color: '#1A1A1A', lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: '0.78rem', color: '#999', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Carousel */}
        <div
          style={{ position: 'relative' }}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Arrows */}
          {[
            { dir: 'prev', icon: <ChevronLeft size={22} />, style: { left: '-20px' } },
            { dir: 'next', icon: <ChevronRight size={22} />, style: { right: '-20px' } },
          ].map(({ dir, icon, style }) => (
            <button key={dir} onClick={() => goTo(dir)} aria-label={dir} style={{
              position: 'absolute', top: '50%', transform: 'translateY(-50%)',
              zIndex: 10, width: '44px', height: '44px', borderRadius: '50%',
              border: '1.5px solid rgba(212,175,55,0.4)', backgroundColor: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#D4AF37', boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              transition: 'all 0.2s', ...style
            }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#D4AF37'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#D4AF37'; }}
            >{icon}</button>
          ))}

          {/* Track */}
          <div style={{ overflow: 'hidden', margin: '0 10px' }}>
            <div style={{
              display: 'flex',
              transform: `translateX(-${current * cardWidth}%)`,
              transition: isAnimating ? 'transform 0.45s cubic-bezier(0.25,0.46,0.45,0.94)' : 'none',
            }}>
              {reviews.map((review, index) => (
                <div key={index} style={{ minWidth: `${cardWidth}%`, padding: '0 10px', boxSizing: 'border-box' }}>
                  <ReviewCard review={review} colorIndex={index} />
                </div>
              ))}
            </div>
          </div>

          {/* Dots */}
          {reviews.length > visibleCards && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '28px' }}>
              {Array.from({ length: maxIndex + 1 }).map((_, i) => (
                <button key={i} onClick={() => { if (!isAnimating) { setIsAnimating(true); setCurrent(i); setTimeout(() => setIsAnimating(false), 400); } }}
                  aria-label={`Go to slide ${i + 1}`}
                  style={{
                    width: current === i ? '24px' : '8px', height: '8px',
                    borderRadius: '4px', border: 'none', padding: 0,
                    backgroundColor: current === i ? '#D4AF37' : '#e0e0e0',
                    transition: 'all 0.3s', cursor: 'pointer'
                  }} />
              ))}
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="text-center mt-5">
          <Link
            to="/reviews"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '14px 32px', border: '1.5px solid #D4AF37', borderRadius: '50px',
              color: '#D4AF37', textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem',
              letterSpacing: '0.5px', transition: 'all 0.25s', backgroundColor: 'transparent',
            }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#D4AF37'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#D4AF37'; }}
          >
            View All Reviews <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

const ReviewCard = ({ review, colorIndex }) => {
  const name = review.display_name || review.name || 'Guest';
  const rating = Number(review.rating) || 5;
  const date = review.created_at
    ? new Date(review.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Recently';

  return (
    <div style={{
      backgroundColor: '#faf8f5', borderRadius: '16px', padding: '28px 24px',
      border: '1px solid rgba(212,175,55,0.12)', position: 'relative',
      height: '100%', minHeight: '200px', boxSizing: 'border-box',
      display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      transition: 'transform 0.2s, box-shadow 0.2s',
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <Quote size={28} color="rgba(212,175,55,0.15)" style={{ position: 'absolute', top: '18px', right: '18px' }} />

      {/* Stars */}
      <div style={{ display: 'flex', gap: '3px', marginBottom: '14px' }}>
        {[...Array(5)].map((_, i) => (
          <Star key={i} size={14} fill={i < rating ? '#D4AF37' : 'none'} color={i < rating ? '#D4AF37' : '#ddd'} />
        ))}
      </div>

      {/* Comment */}
      <p style={{
        fontSize: '0.92rem', color: '#555', lineHeight: 1.85, fontStyle: 'italic',
        marginBottom: '20px', flex: 1,
        display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical', overflow: 'hidden'
      }}>
        "{review.comment}"
      </p>

      {/* Author */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '38px', height: '38px', borderRadius: '50%',
            backgroundColor: avatarColors[colorIndex % avatarColors.length],
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: '0.88rem', flexShrink: 0
          }}>
            {name.charAt(0).toUpperCase()}
          </div>
          <div>
            <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#333', display: 'block' }}>{name}</span>
            <span style={{ fontSize: '0.72rem', color: '#aaa' }}>{date}</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#27AE60', fontSize: '0.7rem', fontWeight: 600 }}>
          <CheckCircle size={12} />
          <span>Verified</span>
        </div>
      </div>
    </div>
  );
};

export default Testimonials;
